import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Link, Type } from 'lucide-react';
import { Button } from './button';

const RichTextEditor = ({ value, onChange, placeholder = "Écrivez votre texte..." }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const isInternalChange = useRef(false);

  // Initialize editor content only once or when value changes externally
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      // Only update if the editor doesn't have focus (external update)
      if (!isFocused && editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value, isFocused]);

  const execCommand = useCallback((command, commandValue = null) => {
    document.execCommand(command, false, commandValue);
    editorRef.current?.focus();
    // Trigger onChange with updated HTML
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleBold = () => execCommand('bold');
  const handleItalic = () => execCommand('italic');
  const handleUnderline = () => execCommand('underline');
  const handleStrikethrough = () => execCommand('strikeThrough');
  const handleOrderedList = () => execCommand('insertOrderedList');
  const handleUnorderedList = () => execCommand('insertUnorderedList');
  
  const handleHeading = (level) => {
    execCommand('formatBlock', `h${level}`);
  };

  const handleLink = () => {
    const url = prompt('Entrez l\'URL du lien:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  return (
    <div className="border border-purple-500/30 rounded-lg overflow-hidden bg-black/50">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-black/30 border-b border-purple-500/30">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBold}
          className="h-8 w-8 p-0 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50"
          title="Gras (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleItalic}
          className="h-8 w-8 p-0 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50"
          title="Italique (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleUnderline}
          className="h-8 w-8 p-0 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50"
          title="Souligné (Ctrl+U)"
        >
          <Underline className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleStrikethrough}
          className="h-8 w-8 p-0 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50"
          title="Barré"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-purple-500/30 mx-1 self-center" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => handleHeading(2)}
          className="h-8 px-2 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50 text-xs font-bold"
          title="Titre"
        >
          <Type className="h-4 w-4 mr-1" />
          H2
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => handleHeading(3)}
          className="h-8 px-2 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50 text-xs font-bold"
          title="Sous-titre"
        >
          <Type className="h-3 w-3 mr-1" />
          H3
        </Button>

        <div className="w-px h-6 bg-purple-500/30 mx-1 self-center" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleUnorderedList}
          className="h-8 w-8 p-0 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50"
          title="Liste à puces"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleOrderedList}
          className="h-8 w-8 p-0 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50"
          title="Liste numérotée"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-purple-500/30 mx-1 self-center" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleLink}
          className="h-8 w-8 p-0 text-purple-300 hover:text-purple-100 hover:bg-purple-900/50"
          title="Insérer un lien"
        >
          <Link className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="min-h-[150px] p-4 text-white focus:outline-none rich-text-content"
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
        style={{
          minHeight: '150px',
        }}
      />

      <style>{`
        .rich-text-content:empty:before {
          content: attr(data-placeholder);
          color: rgba(156, 163, 175, 0.5);
          pointer-events: none;
        }
        .rich-text-content h1 {
          font-size: 1.75rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          color: #c4b5fd;
        }
        .rich-text-content h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          color: #c4b5fd;
        }
        .rich-text-content h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          color: #c4b5fd;
        }
        .rich-text-content p {
          margin-bottom: 0.5rem;
        }
        .rich-text-content ul {
          list-style-type: disc;
          margin-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .rich-text-content ol {
          list-style-type: decimal;
          margin-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .rich-text-content a {
          color: #a855f7;
          text-decoration: underline;
        }
        .rich-text-content b, .rich-text-content strong {
          font-weight: bold;
        }
        .rich-text-content i, .rich-text-content em {
          font-style: italic;
        }
        .rich-text-content u {
          text-decoration: underline;
        }
        .rich-text-content strike, .rich-text-content s {
          text-decoration: line-through;
        }
      `}</style>
    </div>
  );
};

export { RichTextEditor };
